import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, IconButton, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
  Menu, MenuItem, ListItemIcon, ListItemText, LinearProgress, Snackbar,
  Stack, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import TranslateIcon from '@mui/icons-material/Translate';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import PublishIcon from '@mui/icons-material/Publish';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import apiClient from '../../services/apiClient';
import { getLanguageDisplayName, SUPPORTED_LANGUAGES } from '../../config/languagesConfig';

/**
 * Content Matrix (admin overhaul) — a path-level grid of stories × languages.
 * Each cell shows translation + audio status at a glance; column/row menus run
 * batch translate/audio across the path (orchestrated client-side over the
 * existing per-story endpoints, with progress). Replaces the one-story-at-a-time
 * authoring grind.
 */

const langLabel = (code) => getLanguageDisplayName?.(code) || code;
const langFlag = (code) => SUPPORTED_LANGUAGES?.[code]?.flag || '';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Word-matching marker per cell (tappable words + per-sentence English).
const WM = {
  done: { color: 'success.main', label: 'Word matching done' },
  draft: { color: 'warning.main', label: 'Word matching partly built' },
  stale: { color: 'error.main', label: 'Word matching out of date (text changed)' },
  none: { color: null, label: 'No word matching' },
};

// Cell visual: returns { color, label, glyph }
function cellStyle(cell) {
  if (!cell || !cell.has_content) return { bg: 'transparent', dot: null, title: 'No translation' };
  if (!cell.has_audio) return { bg: 'warning.light', dot: 'warning.main', title: 'Translated · no audio' };
  if (cell.stale) return { bg: 'warning.light', dot: 'error.main', title: 'Audio stale (text edited after audio)' };
  if (cell.at_risk_long) return { bg: 'success.light', dot: 'error.main', title: 'Translated + audio · long (regenerate to be safe)' };
  return { bg: 'success.light', dot: 'success.main', title: 'Translated + audio' };
}

export default function ContentMatrix() {
  const { pathId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState('');
  const [batch, setBatch] = useState(null); // { label, done, total }
  const [colMenu, setColMenu] = useState({ anchor: null, lang: null });

  const fetchMatrix = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.admin.getPathMatrix(pathId);
      setData(res.data);
    } catch (e) {
      console.error('matrix load failed', e);
      setError('Could not load the content matrix.');
    } finally {
      setLoading(false);
    }
  }, [pathId]);

  useEffect(() => { fetchMatrix(); }, [fetchMatrix]);

  // Run an async fn over a list sequentially, with progress. Per-item errors are
  // collected, not fatal.
  const runBatch = async (label, items, fn) => {
    if (!items.length) { setSnack('Nothing to do — already complete.'); return; }
    setBatch({ label, done: 0, total: items.length });
    let failures = 0;
    for (let i = 0; i < items.length; i++) {
      try { await fn(items[i]); } catch (e) { failures += 1; console.error(label, 'item failed', e); }
      setBatch({ label, done: i + 1, total: items.length });
    }
    setBatch(null);
    setSnack(failures ? `${label}: done with ${failures} issue(s).` : `${label}: done.`);
    fetchMatrix();
  };

  const stories = data?.stories || [];
  const languages = data?.languages || [];

  // Column batch: translate this language across all stories missing it.
  const translateColumn = async (lang) => {
    const targets = stories.filter((s) => !s.cells?.[lang]?.has_content).map((s) => s.story_id);
    await runBatch(`Translate ${langLabel(lang)}`, targets,
      (sid) => apiClient.admin.generateTranslations(sid, [lang]));
  };
  // Column batch: generate audio for this language across stories that have text but no audio.
  const audioColumn = async (lang) => {
    const targets = stories.filter((s) => s.cells?.[lang]?.has_content && !s.cells?.[lang]?.has_audio)
      .map((s) => s.story_id);
    await runBatch(`Generate ${langLabel(lang)} audio`, targets,
      (sid) => apiClient.admin.generateAudio(sid, [lang]));
  };
  // Row batch: generate all missing audio for one story.
  const audioRow = async (story) => {
    if (!story.missing_audio?.length) { setSnack('No missing audio for this story.'); return; }
    await runBatch(`Audio for "${story.title}"`, [story],
      (s) => apiClient.admin.generateAudio(s.story_id, s.missing_audio));
  };
  // Column batch: build word matching for this language wherever it isn't done.
  // Each story is built chunk by chunk (one short request per ~40 words).
  const wordMatchingColumn = async (lang) => {
    const targets = stories
      .filter((s) => s.cells?.[lang]?.has_content && s.cells?.[lang]?.word_matching !== 'done')
      .map((s) => ({ id: s.story_id, reset: s.cells?.[lang]?.word_matching === 'stale' }));
    await runBatch(`Word matching ${langLabel(lang)}`, targets, async ({ id, reset }) => {
      let first = true;
      let failures = 0;
      for (let guard = 0; guard < 500; guard++) {
        try {
          const res = await apiClient.admin.buildWordMatching(id, lang, { reset: reset && first });
          first = false;
          failures = 0;
          if (res.data.done) return;
        } catch (e) {
          if (++failures > 2) throw e;
          await sleep(4000 * failures);
        }
      }
    });
  };
  // Path-level: generate all missing audio across the path.
  const audioAllMissing = async () => {
    const targets = stories.filter((s) => s.missing_audio?.length);
    await runBatch('Generate all missing audio', targets,
      (s) => apiClient.admin.generateAudio(s.story_id, s.missing_audio));
  };

  const moveStory = async (index, dir) => {
    const ids = stories.map((s) => s.story_id);
    const j = dir === 'up' ? index - 1 : index + 1;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    try {
      await apiClient.admin.reorderStories(pathId, ids);
      fetchMatrix();
    } catch (e) {
      setSnack('Reorder failed');
    }
  };

  const publishStory = async (story) => {
    try {
      await apiClient.admin.publishStory(story.story_id);
      setSnack(`Published "${story.title}".`);
      fetchMatrix();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Publish failed';
      setSnack(msg);
    }
  };
  const publishPath = async () => {
    try {
      const res = await apiClient.admin.publishPath(pathId);
      const skipped = res.data?.skipped?.length || 0;
      setSnack(skipped ? `Path published; ${skipped} story(ies) skipped (not ready).` : 'Path published.');
      fetchMatrix();
    } catch (e) {
      setSnack(e?.response?.data?.error || 'Publish path failed');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }
  if (error) {
    return <Box sx={{ p: 3 }}><Alert severity="error" action={<Button onClick={fetchMatrix}>Retry</Button>}>{error}</Alert></Box>;
  }

  const readyCount = stories.filter((s) => s.ready).length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(`/admin/paths/${pathId}`)} aria-label="Back">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>{data?.path?.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            Content matrix · {stories.length} stories · {readyCount} ready
            {data?.path?.is_published ? ' · published' : ' · draft'}
          </Typography>
        </Box>
        <Tooltip title="Refresh"><IconButton onClick={fetchMatrix}><RefreshIcon /></IconButton></Tooltip>
        <Button variant="outlined" startIcon={<GraphicEqIcon />} onClick={audioAllMissing} sx={{ textTransform: 'none' }}>
          Fill missing audio
        </Button>
        <Button variant="contained" startIcon={<PublishIcon />} onClick={publishPath} sx={{ textTransform: 'none' }}>
          Publish ready
        </Button>
      </Stack>

      {batch && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>{batch.label} — {batch.done}/{batch.total}</Typography>
          <LinearProgress variant="determinate" value={Math.round((batch.done / batch.total) * 100)} />
        </Paper>
      )}

      {/* Legend */}
      <Stack direction="row" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <LegendDot bg="transparent" border label="No translation" />
        <LegendDot bg="warning.light" label="Translated · no audio" />
        <LegendDot bg="success.light" label="Translated + audio" />
        <LegendDot bg="success.light" dot="error.main" label="Stale / long (regenerate)" />
        <LegendBar color="success.main" label="Word matching done" />
        <LegendBar color="warning.main" label="Partly built" />
        <LegendBar color="error.main" label="Out of date" />
      </Stack>

      {/* Matrix */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper', fontWeight: 700, minWidth: 240 }}>
                Story
              </TableCell>
              {languages.map((lang) => (
                <TableCell key={lang} align="center" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                    <span>{langFlag(lang)} {lang.toUpperCase()}</span>
                    <IconButton size="small" onClick={(e) => setColMenu({ anchor: e.currentTarget, lang })}>
                      <MoreVertIcon fontSize="inherit" />
                    </IconButton>
                  </Stack>
                  {data?.coverage?.[lang] && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {data.coverage[lang].with_audio}/{data.coverage[lang].translated || 0}
                    </Typography>
                  )}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stories.map((story, index) => (
              <TableRow key={story.story_id} hover>
                <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {story.ready
                      ? <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                      : <Box sx={{ width: 18 }} />}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{story.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        #{story.order_index}{story.section ? ` · ${story.section}` : ''} · {story.is_published ? 'published' : 'draft'}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                {languages.map((lang) => {
                  const cell = story.cells?.[lang];
                  const st = cellStyle(cell);
                  const wm = cell?.has_content ? WM[cell.word_matching || 'none'] : null;
                  return (
                    <TableCell key={lang} align="center" sx={{ p: 0.5 }}>
                      <Tooltip title={wm ? `${st.title} · ${wm.label}` : st.title}>
                        <Box
                          onClick={() => navigate(cell?.has_content
                            ? `/admin/stories/${story.story_id}/word-matching?lang=${lang}`
                            : `/admin/stories/${story.story_id}/translations`)}
                          sx={{
                            mx: 'auto', width: 26, height: 26, borderRadius: 1, cursor: 'pointer',
                            bgcolor: st.bg,
                            border: st.bg === 'transparent' ? '1px dashed' : 'none',
                            borderColor: 'divider',
                            position: 'relative',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          {cell?.is_auto_translated && cell?.has_content && (
                            <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary' }}>A</Typography>
                          )}
                          {st.dot && (
                            <Box sx={{ position: 'absolute', top: 2, right: 2, width: 6, height: 6, borderRadius: '50%', bgcolor: st.dot }} />
                          )}
                          {wm?.color && (
                            <Box sx={{ position: 'absolute', bottom: 2, left: 3, right: 3, height: 3, borderRadius: 2, bgcolor: wm.color }} />
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  );
                })}
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Stack>
                      <Tooltip title="Move up">
                        <span>
                          <IconButton size="small" sx={{ p: 0 }} disabled={index === 0} onClick={() => moveStory(index, 'up')}>
                            <KeyboardArrowUpIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <span>
                          <IconButton size="small" sx={{ p: 0 }} disabled={index === stories.length - 1} onClick={() => moveStory(index, 'down')}>
                            <KeyboardArrowDownIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                    <Tooltip title="Edit / translations">
                      <IconButton size="small" onClick={() => navigate(`/admin/stories/${story.story_id}/translations`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Generate missing audio">
                      <span>
                        <IconButton size="small" disabled={!story.missing_audio?.length} onClick={() => audioRow(story)}>
                          <GraphicEqIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={story.ready ? 'Publish story' : 'Not ready (needs audio)'}>
                      <span>
                        <IconButton size="small" color="primary" disabled={!story.ready || story.is_published} onClick={() => publishStory(story)}>
                          <PublishIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {stories.length === 0 && (
              <TableRow>
                <TableCell colSpan={languages.length + 2} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  No stories in this path yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Column batch menu */}
      <Menu anchorEl={colMenu.anchor} open={Boolean(colMenu.anchor)} onClose={() => setColMenu({ anchor: null, lang: null })}>
        <MenuItem onClick={() => { const l = colMenu.lang; setColMenu({ anchor: null, lang: null }); translateColumn(l); }}>
          <ListItemIcon><TranslateIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Translate {colMenu.lang && langLabel(colMenu.lang)} (all stories)</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { const l = colMenu.lang; setColMenu({ anchor: null, lang: null }); audioColumn(l); }}>
          <ListItemIcon><GraphicEqIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Generate {colMenu.lang && langLabel(colMenu.lang)} audio (all stories)</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { const l = colMenu.lang; setColMenu({ anchor: null, lang: null }); wordMatchingColumn(l); }}>
          <ListItemIcon><SpellcheckIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Build {colMenu.lang && langLabel(colMenu.lang)} word matching (all stories)</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { const s = stories[0]; setColMenu({ anchor: null, lang: null }); if (s) navigate(`/admin/stories/${s.story_id}/translations`); }}>
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Open production center</ListItemText>
        </MenuItem>
      </Menu>

      <Snackbar open={Boolean(snack)} autoHideDuration={4000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}

function LegendBar({ color, label }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box sx={{ width: 16, height: 16, borderRadius: 0.5, border: '1px solid', borderColor: 'divider', position: 'relative' }}>
        <Box sx={{ position: 'absolute', bottom: 2, left: 2, right: 2, height: 3, borderRadius: 2, bgcolor: color }} />
      </Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Stack>
  );
}

function LegendDot({ bg, dot, border, label }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: bg, border: border ? '1px dashed' : 'none', borderColor: 'divider', position: 'relative' }}>
        {dot && <Box sx={{ position: 'absolute', top: 1, right: 1, width: 5, height: 5, borderRadius: '50%', bgcolor: dot }} />}
      </Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Stack>
  );
}
